import { detectAllFaces, type FaceDetection, nets, resizeResults, TinyFaceDetectorOptions } from 'face-api.js';
import { type MutableRefObject, useEffect } from 'react';
import { getLogger } from '@/idv/utils';
import { useFaceModel } from '../../../hooks/use-face-model-loader';
import type { Resolution, VideoRef } from '../types';
import { isFaceCloseEnough, isFaceInTheFrame } from '../utils/face-utils';
import getSourceDimensions from '../utils/get-source-dimensions';

const { logInfo, logWarn } = getLogger({ location: 'use-face-detection' });

export enum FaceStatus {
  OK = 'ok',
  faceTooFar = 'face-too-far',
  faceOutsideTheFrame = 'face-outside-the-frame',
  headNotStraight = 'head-not-straight',
  detecting = 'detecting',
  error = 'error',
}

const useFaceDetection = () => {
  const { modelsLoaded, modelsUnavailable, reportInferenceFailure } = useFaceModel();

  const options = new TinyFaceDetectorOptions();

  useEffect(() => {
    function checkMemoryUsage() {
      // @ts-ignore - performance.memory is Chrome-specific
      if (performance?.memory) {
        // @ts-ignore - performance.memory is Chrome-specific
        const memoryInfo = performance.memory;
        const usedHeapSize = memoryInfo.usedJSHeapSize;
        const totalHeapSize = memoryInfo.totalJSHeapSize;
        const jsHeapSizeLimit = memoryInfo.jsHeapSizeLimit;

        logInfo(
          `usedHeapSize: ${usedHeapSize}, 
          totalHeapSize: ${totalHeapSize}, 
          jsHeapSizeLimit: ${jsHeapSizeLimit}, 
          usedHeapSize / totalHeapSize: ${usedHeapSize / totalHeapSize}, 
          usedHeapSize / jsHeapSizeLimit: ${usedHeapSize / jsHeapSizeLimit}`,
        );
      }
    }

    checkMemoryUsage();
  }, []);

  const getFaceStatus = async (
    canvasRef: MutableRefObject<HTMLCanvasElement | undefined>,
    videoRef: VideoRef,
    {
      width,
      height,
      frameDimensions,
      videoResolution,
    }: {
      width: number;
      height: number;
      frameDimensions: {
        width: number;
        height: number;
        frameOffsetX: number;
        frameOffsetY: number;
      };
      videoResolution: Resolution | undefined;
    },
  ) => {
    // `error` reads as "Try capturing manually"; `detecting` would leave "Detecting your
    // face..." on screen for a detector that is never coming back.
    if (modelsUnavailable) {
      return FaceStatus.error;
    }

    if (!modelsLoaded) {
      logInfo('Models not loaded - returning');
      return FaceStatus.detecting;
    }

    try {
      if (!nets.tinyFaceDetector.params || !nets.faceLandmark68Net.params) {
        logWarn('Face model params not found in memory, reloading...');
        return FaceStatus.detecting;
      }

      // Redundant check to avoid errors
      if (!videoRef.current) {
        logWarn('Video element or dimensions are missing');
        return FaceStatus.detecting;
      }

      if (!videoResolution?.width || !videoResolution.height) {
        logWarn('Video resolution is missing');
        return FaceStatus.detecting;
      }

      const faceDetectionCanvas = canvasRef.current;
      if (!faceDetectionCanvas) {
        logWarn('Face detection canvas not found');
        return FaceStatus.detecting;
      }

      faceDetectionCanvas.width = width;
      faceDetectionCanvas.height = height;

      const faceDetectionCtx = canvasRef.current?.getContext('2d', { willReadFrequently: true });
      if (!faceDetectionCtx) {
        logWarn('Face detection context not found');
        return FaceStatus.detecting;
      }
      const { sx, sy, sWidth, sHeight } = getSourceDimensions({
        videoRef,
        videoResolution,
        desiredImageWidth: width,
        desiredImageHeight: height,
        centerOffsetX: 0,
        centerOffsetY: 0,
      });
      faceDetectionCtx.clearRect(0, 0, width, height);
      faceDetectionCtx.drawImage(
        videoRef.current,
        sx,
        sy,
        sWidth,
        sHeight,
        0,
        0,
        faceDetectionCanvas.clientWidth,
        faceDetectionCanvas.clientHeight,
      );

      // face-api's ComposableTask.then ignores the onrejected `await` passes it, so awaiting a
      // task never settles once tfjs throws and the rejection escapes unhandled instead of
      // reaching this catch. detectSingleFace().run() awaits such a task internally and has the
      // same hole; DetectAllFacesTask.run() is the only entry point returning a real promise.
      const detections = await detectAllFaces(faceDetectionCanvas, options).run();
      const detection = detections.reduce<FaceDetection | undefined>(
        (best, candidate) => (best && best.score >= candidate.score ? best : candidate),
        undefined,
      );

      if (!detection) {
        return FaceStatus.detecting;
      }

      if (!detection?.box) {
        return FaceStatus.detecting;
      }

      const {
        box: { width: detectionBoxWidth, height: detectionBoxHeight },
      } = detection;

      if (!detectionBoxWidth || !detectionBoxHeight) {
        return FaceStatus.detecting;
      }

      const { width: frameWidth, height: frameHeight, frameOffsetX, frameOffsetY } = frameDimensions;

      const resizedDetections = resizeResults(detection, { width, height });

      if (!resizedDetections?.box) {
        return FaceStatus.detecting;
      }

      const face = resizedDetections.box;

      const faceX = Math.floor(face.x);
      const faceY = Math.floor(face.y);
      const faceWidth = Math.floor(face.width);
      const faceHeight = Math.floor(face.height);

      // Order matters
      // First priority: face should be close enough
      if (!isFaceCloseEnough({ frameWidth, frameHeight, faceWidth, faceHeight })) {
        return FaceStatus.faceTooFar;
      }

      // Second priority: face should be inside the frame
      if (
        !isFaceInTheFrame({
          videoWidth: width,
          videoHeight: height,
          frameWidth,
          frameHeight,
          faceWidth,
          faceHeight,
          faceX,
          faceY,
          frameOffsetX,
          frameOffsetY,
        })
      ) {
        return FaceStatus.faceOutsideTheFrame;
      }

      // // Third priority: head should be straight pointing to the camera. TODO: removed temporarily for testing
      // const angle = calculateFaceAngle(result.landmarks);
      // if (!isHeadStraight(angle)) return FaceStatus.headNotStraight;

      return FaceStatus.OK;
    } catch (error) {
      reportInferenceFailure(error);
      return FaceStatus.error;
    }
  };

  return { getFaceStatus };
};

export default useFaceDetection;
